import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { MaskedLine, Overline, Reveal } from "@/components/Reveal";

const IMG = {
  hero: "/images/dj3.webp",
  hero2: "/images/dj9.webp",
};

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <main data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative h-screen overflow-hidden">
        <motion.div style={{ y: bgY }} className="absolute inset-0 scale-110">
          <img
            src={IMG.hero}
            alt="Dream Entertainment DJ under purple lights"
            className="w-full h-full object-cover"
          />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/40 to-background" />

        <motion.div
          style={{ opacity: fade }}
          className="relative z-10 h-full max-w-7xl mx-auto px-6 sm:px-12 lg:px-20 flex flex-col justify-center"
        >
          <Reveal y={16}>
            <Overline data-testid="hero-overline">
              Dream Entertainment · DJ &amp; Event Experiences
            </Overline>
          </Reveal>
          <h1 className="font-serif text-5xl sm:text-7xl lg:text-8xl tracking-tight leading-[0.95] mt-8">
            <MaskedLine delay={0.2}>We don&rsquo;t just</MaskedLine>
            <MaskedLine delay={0.35}>play music.</MaskedLine>
            <MaskedLine delay={0.5}>
              We create <em className="text-primary">dreams.</em>
            </MaskedLine>
          </h1>
          <Reveal delay={0.75} className="mt-10 max-w-lg">
            <p className="text-base md:text-lg text-foreground/80 leading-relaxed">
              Weddings, sangeets, baby showers, birthdays and anniversaries.
              Wherever your celebration lives, we bring the soundtrack it
              deserves.
            </p>
          </Reveal>
          <Reveal delay={0.9} className="mt-10 flex flex-wrap gap-4">
            <Link
              to="/booking"
              data-testid="hero-book-btn"
              className="rounded-full bg-primary text-primary-foreground px-8 py-4 text-sm font-medium tracking-widest uppercase transition-[transform,box-shadow] duration-300 hover:scale-105 hover:shadow-[0_0_40px_rgba(255,0,127,0.4)]"
            >
              Book Your Date
            </Link>
            <Link
              to="/packages"
              data-testid="hero-packages-btn"
              className="rounded-full border border-white/20 px-8 py-4 text-sm font-medium tracking-widest uppercase text-foreground transition-colors duration-300 hover:border-primary hover:text-primary"
            >
              Explore Packages
            </Link>
          </Reveal>
        </motion.div>

        <motion.div
          style={{ opacity: fade }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 text-foreground/50"
        >
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ repeat: Infinity, duration: 2 }}
          >
            <ChevronDown size={28} data-testid="hero-scroll-indicator" />
          </motion.div>
        </motion.div>
      </section>

    </main>
  );
}
